import { FileUploadPayloadType } from '@astronautsid/wpe-utils'

import AxiosInstance from 'config/apiServiceApiGateway'
import { ServiceInternalFileRules, ServiceInternalFileUpload } from 'config/apiServiceFile'

import {
  GetSKPListResponseInterface,
  PaginationInterface,
} from 'features/Enterprise/SKP/@types/sourcingTypes'

export interface SKPFilesInterface {
  attachment: string
  create_role?: string
  created_at: string
  created_by: string
  document_name: string
  id: number
}

export interface SKPProductsInterface {
  ads_revenue_amount: number
  ads_revenue_cash_gift: string
  barcode: string
  campaign_name: string
  debit_notes: string
  detail_gift_notes: string
  free_product_barcode: string
  free_product_buy: number
  free_product_get_free: number
  free_product_id: number
  free_product_promo_name: string
  free_product_quantity: number
  free_product_sku_name: string
  free_product_uom: string
  freebies_po_number: string
  freebies_total_value: number
  freebies_value_per_uom: number
  id: number
  invoice_number: string
  paid_date: string
  product_id: number
  promotion_quantity: number
  rafaksi_base_discount: string
  rafaksi_price_per_uom: number
  rafaksi_quantity_actual: number
  rafaksi_quantity_max_supported: number
  rafaksi_rebate_actual: number
  rafaksi_rebate_budget: number
  rafaksi_rebate_per_uom: number
  rafaksi_rebate_percentage: number
  remarks: string
  skp_no: string
  sku_name: string
  uom: string
  rafaksi_tiering: {
    rafaksi_tier?: number
    rafaksi_rebate_percentage?: string
    rafaksi_rebate_per_uom?: number
    rafaksi_minimum_purchase?: number
    rafaksi_qty_per_uom?: number
    rafaksi_budget_per_uom?: number
    rafaksi_actual_claim_per_uom?: number
    rafaksi_claim_tier_qty?: number
  }[]
  rafaksi_minimum_purchase?: number
  rafaksi_qty_per_uom?: number
  rafaksi_budget_per_uom?: number
  quota_per_day:
    | number
    | {
        Float64: number
        Valid: boolean
      }
    | null
  quota_per_day_value: string
}

export interface SKPDetailsInterface {
  amount: number
  approved_actual_claim_at: string
  approved_actual_claim_by: string
  archived_at: string
  archived_by: string
  astro_pic_role: string
  astro_pic_signed: string
  attachments: string | null
  billing_address: string
  billing_name: string
  billing_npwp: string
  campaign_name: string
  collection_vendor_pic: string
  created_at: string
  created_by: string
  deleted_at: string | null
  deleted_by: string | null
  details: string | null
  end_date: string
  end_date_campaign: string
  id: number
  is_approved_actual_claim: boolean
  is_campaign_download: boolean
  is_uploaded_actual_claim: boolean
  is_uploaded_proof: boolean
  is_uploaded_signed_skp: boolean
  is_verified: boolean
  notes_claim: string
  paid_date: string
  promotion_type: string
  purchase_order: string
  reimbursement_method: string
  reimbursement_number: string
  reject_at: string
  reject_by: string
  reject_reason: string
  reopen_at: string
  reopen_by: string
  skp_no: string
  sourcing_astro_pic: string
  start_date: string
  start_date_campaign: string
  status: string
  submitted_at: string
  submitted_by: string
  top_duration: number
  updated_at: string
  updated_by: string
  uploaded_actual_claim_at: string
  uploaded_actual_claim_by: string
  url_signed_skp: string
  vendor_address: string
  vendor_email: string
  vendor_id: number
  vendor_name: string
  vendor_phone_number: string
  vendor_pic_finance: string
  vendor_pic_finance_phone: string
  vendor_pic_role: string
  vendor_pic_signed: string
  verified_at: string
  verified_by: string
  budget_type: string
  campaign_cluster: string
}

export interface SKPReimbursementInterface {
  amount: number
  created_at: string
  created_by: string
  deleted_at: string | null
  deleted_by: string | null
  id: number
  number: string
  paid_date: string
  skp: SKPDetailsInterface
  skp_id: number
  type: string
  updated_at: string
  updated_by: string
}

export interface SKPProductDetailsInterface {
  barcode: string
  product_name: string
  uom: string
}

export interface SKPDataQueryInterface {
  limit: number
  offset: number
  total: number
}

export interface SKPProductPayloadInterface {
  id?: number
  barcode?: string
  product_id?: number
  promotion_quantity?: number
  rafaksi_base_discount?: string
  rafaksi_price_per_uom?: number
  rafaksi_rebate_budget?: number
  rafaksi_rebate_per_uom?: number
  rafaksi_rebate_percentage?: number
  remarks?: string
  sku_name?: string
  uom?: string
  rafaksi_tiering?: {
    rafaksi_tier?: number
    rafaksi_rebate_percentage?: number
    rafaksi_rebate_per_uom?: number
    rafaksi_buying_tier_qty?: number
    rafaksi_budget?: number
  }[]
  quota_per_day?: number
}

export interface GetSKPFilesResponseInterface {
  data: SKPFilesInterface[]
  query: SKPDataQueryInterface
}

export interface GetSKPProductsResponseInterface {
  data: SKPProductsInterface[]
  query: SKPDataQueryInterface
}

export interface GetSKPReimbursementInterface {
  data: SKPReimbursementInterface[]
  query: SKPDataQueryInterface
}

export interface PostSKPProductInterface {
  data: {
    barcode: string
    product_name: string
    uom: string
  }
}

export interface PostItemReimbursementInterface {
  amount: number
  number: string
  paid_date: string
  type: string
}

export interface SKPVendorInterface {
  id: number
  company_name: string
}

export interface FetchVendorsInterface {
  data: SKPVendorInterface[]
}

export interface PostSKPInterface {
  id?: number
  campaign_name: string
  reimbursement_method: string
  astro_pic_signed: string
  astro_pic_role: string
  sourcing_astro_pic: string
  collection_vendor_pic: string
  vendor_pic_signed: string
  vendor_pic_role: string
  vendor_pic_finance: string
  vendor_pic_finance_phone: string
  billing_name: string
  billing_address: string
  billing_npwp: string
  vendor_name?: string
  vendor_id?: number
  vendor_email?: string
  vendor_phone_number: string
  vendor_address: string
  promotion_type: string
  start_date: string
  end_date: string
  top_duration: number
  notes_claim: string
  start_date_campaign: string
  end_date_campaign: string
  campaign_cluster: string
}

export interface PostInternalSKPInterface {
  id?: number
  campaign_name: string
  promotion_type: string
  vendor_id?: number
  vendor_name?: string
  end_date: string
  end_date_campaign: string
  start_date: string
  start_date_campaign: string
  notes_claim: string
  budget_type: string
  campaign_cluster: string
}

export type CampaignClusterType = {
  cluster: string
  id: number
}

export interface BaseDiscountsResponseInterface {
  datas: {
    id: string
    name: string
  }[]
  error: {
    message: string
    status: boolean
    code: number
  }
}

export interface SKPProductV1DetailsInterface {
  datas: {
    barcode: string
    product_id: 0
    sku_name: string
    uom: string
  }[]
  error: {
    message: string
    status: boolean
    code: number
  }
}

export const fetchSKPListAPI = (query: string) =>
  AxiosInstance.Get<GetSKPListResponseInterface, true>({
    url: `/commercial/internal/:version/skp?${query}`,
    version: 'v1',
  })

type PaginatedListBodyInterface<T> = {
  data?: T[] | Record<string, unknown>
  pagination?: PaginationInterface
  query?: SKPDataQueryInterface
  skp_details?: T[]
  result?: T[]
  skp_attachments?: T[]
}

const ECHO_LIST_KEYS = [
  'result',
  'skp_attachments',
  'skp_details',
  'data',
  'datas',
  'items',
] as const

const extractListFromRecord = <T>(record: Record<string, unknown>): T[] | null => {
  const matchedKey = ECHO_LIST_KEYS.find((key) => Array.isArray(record[key]))
  return matchedKey ? (record[matchedKey] as T[]) : null
}

const extractEchoListItems = <T>(body: PaginatedListBodyInterface<T>): T[] => {
  if (Array.isArray(body.data)) {
    return body.data
  }

  if (body.data && typeof body.data === 'object') {
    const nestedItems = extractListFromRecord<T>(body.data as Record<string, unknown>)
    if (nestedItems) {
      return nestedItems
    }
  }

  const matchedKey = ECHO_LIST_KEYS.find((key) => {
    const value = body[key as keyof PaginatedListBodyInterface<T>]
    return (
      Array.isArray(value) ||
      (value &&
        typeof value === 'object' &&
        extractListFromRecord<T>(value as Record<string, unknown>))
    )
  })

  if (!matchedKey) {
    return []
  }

  const value = body[matchedKey as keyof PaginatedListBodyInterface<T>]
  if (Array.isArray(value)) {
    return value as T[]
  }

  if (value && typeof value === 'object') {
    return extractListFromRecord<T>(value as Record<string, unknown>) ?? []
  }

  return []
}

const extractEchoPagination = <T>(
  body: PaginatedListBodyInterface<T>,
): PaginationInterface | undefined => {
  if (body.pagination) {
    return body.pagination
  }

  if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
    const nestedPagination = (body.data as Record<string, unknown>).pagination
    if (nestedPagination && typeof nestedPagination === 'object') {
      return nestedPagination as PaginationInterface
    }
  }

  return undefined
}

export const mapPaginatedListToLegacyQuery = <T>(
  body: PaginatedListBodyInterface<T>,
  pageSize: number,
  offset: number,
): { data: T[]; query: SKPDataQueryInterface } => {
  if (body.query && Array.isArray(body.data)) {
    return { data: body.data, query: body.query }
  }

  const items = extractEchoListItems(body)
  const pagination = extractEchoPagination(body)

  return {
    data: items,
    query: {
      limit: pageSize,
      offset,
      total: pagination?.total_elements || pagination?.number_of_elements || items.length,
    },
  }
}

export const fetchSKPProducts = (skpId: number, offset: number, limit: string, sku_name = '') => {
  const pageSize = Number(limit) || 5
  const pageIndex = pageSize ? Math.floor(offset / pageSize) : 0
  return AxiosInstance.Get<PaginatedListBodyInterface<SKPProductsInterface>, true>({
    url: `/commercial/internal/v1/skps/${skpId}/details/list?page_size=${pageSize}&page_index=${pageIndex}&sku_name=${sku_name}`,
  })
}

export const upsertSKPProduct = (id: string, data: SKPProductPayloadInterface) =>
  AxiosInstance.Put<PostSKPProductInterface, true>({
    url: `/commercial/internal/:version/skps/${id}/details`,
    data,
    version: 'v1',
  })

export const deleteSKPProduct = (skpId: number, id: number) =>
  AxiosInstance.Delete<PostSKPProductInterface, true>({
    url: `/commercial/internal/v1/skps/${skpId}/details/${id}`,
  })

const fetchSKPAttachments = (
  skpId: number,
  offset: number,
  attachmentType: 'agreement' | 'proof',
) => {
  const pageSize = 5
  const pageIndex = Math.floor(offset / pageSize)
  return AxiosInstance.Get<PaginatedListBodyInterface<SKPFilesInterface>, true>({
    url: `/skp/internal/v1/skp/${skpId}/attachment?page_size=${pageSize}&page_index=${pageIndex}&attachment_type=${attachmentType}`,
  })
}

export const fetchSKPAgreements = (skpId: number, offset: number) =>
  fetchSKPAttachments(skpId, offset, 'agreement')

export const deleteSKPAttachment = (skpId: number, id: number) =>
  AxiosInstance.Delete({
    url: `/skp/internal/v1/skp/${skpId}/attachment/${id}`,
  })

export const fetchSKPActualClaims = (skpId: number, offset: number) => {
  const pageSize = 5
  const pageIndex = Math.floor(offset / pageSize)
  return AxiosInstance.Get<PaginatedListBodyInterface<SKPProductsInterface>, true>({
    url: `/commercial/internal/v1/skps/${skpId}/actual-claims/list?page_size=${pageSize}&page_index=${pageIndex}&sku_name=`,
  })
}

export const fetchSKPReimbursements = (skpId: number, offset: number) => {
  const pageSize = 5
  return AxiosInstance.Get<PaginatedListBodyInterface<SKPReimbursementInterface>, true>({
    url: `/commercial/internal/v1/skps/${skpId}/reimbursements?page_size=${pageSize}&page_index=${Math.floor(
      offset / pageSize,
    )}`,
  })
}

export const fetchSKProofFiles = (skpId: number, offset: number) =>
  fetchSKPAttachments(skpId, offset, 'proof')

export const postApproveActualClaim = (skpId: number, isApprove: boolean) =>
  AxiosInstance.Put({
    url: `/skp/internal/v1/skps/${skpId}/actual-claim/status`,
    data: { action: isApprove ? 'APPROVE' : 'CANCEL_APPROVE' },
  })

export const postReimbursementItem = (skpId: number, data: PostItemReimbursementInterface) =>
  AxiosInstance.Post({
    url: `/commercial/internal/v1/skps/${skpId}/reimbursements`,
    data,
  })

export const postUploadFile = ({
  skpNumber,
  module,
  data,
  promotionType = '',
}: {
  skpNumber: string
  module: string
  data: FormData
  promotionType?: string
}) =>
  AxiosInstance.Post<string>({
    url: `/enterprise/skp/${module}/${skpNumber}/upload${promotionType}`,
    data,
    config: {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'multipart/form-data',
      },
    },
  })

export const postSKP = (data: PostSKPInterface | PostInternalSKPInterface) =>
  AxiosInstance.Post({
    url: '/commercial/internal/v1/skp?state=default',
    version: 'v1',
    data,
  })

export const deleteReimbursement = (id: number, skpId: number) =>
  AxiosInstance.Delete({
    url: `/commercial/internal/v1/skps/${skpId}/reimbursements/${id}`,
  })

export interface PutActualClaimPayloadInterface {
  id: number
  rafaksi_rebate_actual: number
  rafaksi_quantity_actual: number
  rafaksi_quantity_max_supported: number
}

export const putActualQtyClaimItem = (skpId: number, data: PutActualClaimPayloadInterface) =>
  AxiosInstance.Put({
    url: `/commercial/internal/v1/skps/${skpId}/actual-claims`,
    data,
  })

export const deleteActualQtyClaimItem = (id: number, skpId: number) =>
  AxiosInstance.Patch({
    url: `/commercial/internal/v1/skps/${skpId}/actual-claims/${id}`,
  })

export const fetchCampaignCluster = () =>
  AxiosInstance.Get<{ data: { campaign_clusters: CampaignClusterType[] } }, true>({
    url: '/skp/internal/:version/campaign-clusters',
    version: 'v1',
  })

export const postSubmitSKPFile = (file_url: string, skpId: number) =>
  AxiosInstance.Post({
    url: `/skp/internal/:version/skp/${skpId}/attachment`,
    data: { file_url },
    version: 'v2',
  })

export const postUploadSKPFile = (payload: FileUploadPayloadType[]) =>
  ServiceInternalFileUpload(payload)

export const getUploadFileRules = (typeId: string) => ServiceInternalFileRules(typeId)

export const getBaseDiscounts = () =>
  AxiosInstance.Get<BaseDiscountsResponseInterface, true>({
    url: '/skp/internal/:version/base-discounts',
    version: 'v1',
  })

export const fetchSKPProductDetailsV1 = (id: string) =>
  AxiosInstance.Get<SKPProductV1DetailsInterface, true>({
    url: `/skp/internal/:version/products?product_ids=${id}`,
    version: 'v1',
  })
